/**
 * SnapshotDiffDialog — side-by-side comparison of two snapshots from
 * the same lineage chain.
 *
 * Lifts the visual pattern from RenameTheatreDialog (left pane / center
 * connector / right pane) but the center column is a CHANGE LEDGER
 * instead of a confirm/reject UI — there's nothing for the user to
 * decide here, this is a *read* surface that answers "what's
 * different between snapshot S(n−1) and S(n)?"
 *
 * The diff is computed client-side via `diffSnapshots()` over the two
 * cached IRs. No backend route — these snapshots are already in the
 * React Query cache because the IterationCard fetched them.
 */
import { useSnapshot } from "@features/circuits/hooks/useCircuitIr";
import { CircuitRenderResolver } from "@features/circuits/components/CircuitRenderResolver";
import AddIcon from "@mui/icons-material/AddRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import EditIcon from "@mui/icons-material/EditRounded";
import RemoveIcon from "@mui/icons-material/RemoveRounded";
import RouteIcon from "@mui/icons-material/RouteRounded";
import VerifiedIcon from "@mui/icons-material/VerifiedRounded";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo } from "react";

import {
  type ComponentChange,
  type NetTopologyChange,
  diffSnapshots,
} from "./snapshotDiff";

interface SnapshotDiffDialogProps {
  open: boolean;
  /** Older snapshot — typically `parent_snapshot_id`. */
  leftSnapshotId: string | null;
  /** Newer snapshot — the one the IterationCard or DraftSheet is on. */
  rightSnapshotId: string | null;
  onClose: () => void;
}

export function SnapshotDiffDialog({
  open,
  leftSnapshotId,
  rightSnapshotId,
  onClose,
}: SnapshotDiffDialogProps) {
  const { data: leftSnap, isLoading: leftLoading } = useSnapshot(
    open ? leftSnapshotId : null,
  );
  const { data: rightSnap, isLoading: rightLoading } = useSnapshot(
    open ? rightSnapshotId : null,
  );

  const diff = useMemo(() => {
    if (!leftSnap || !rightSnap) return null;
    return diffSnapshots(leftSnap.ir, rightSnap.ir);
  }, [leftSnap, rightSnap]);

  const isLoading = leftLoading || rightLoading;
  const totals = diff
    ? {
        added: diff.componentsAdded.length,
        removed: diff.componentsRemoved.length,
        changed: diff.componentsChanged.length,
        netsTopo: diff.netsTopologyChanged.length,
      }
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", fontSize: 11 }}
            >
              SNAPSHOT DIFF
            </Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.2, mt: 0.25 }}>
              {leftSnapshotId && rightSnapshotId
                ? `${leftSnapshotId.slice(0, 8)}… → ${rightSnapshotId.slice(0, 8)}…`
                : "Comparing snapshots"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {totals && <DiffSummaryChips totals={totals} />}
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{ ml: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && (
          <Stack sx={{ alignItems: "center", py: 8 }}>
            <CircularProgress size={24} />
          </Stack>
        )}

        {!isLoading && (!leftSnap || !rightSnap) && (
          <Alert severity="info" sx={{ m: 2 }}>
            One of the snapshots couldn't be loaded — it may have been
            garbage-collected. Snapshots in the working buffer expire after 30
            days.
          </Alert>
        )}

        {!isLoading && diff && diff.isClean && (
          <Alert severity="success" sx={{ m: 2 }} icon={<VerifiedIcon />}>
            Semantically identical — the two snapshots have the same components,
            values, and net topology. The mutation may have touched only
            schematic geometry or extras (which the diff ignores by contract).
          </Alert>
        )}

        {!isLoading && diff && !diff.isClean && leftSnap && rightSnap && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 320px 1fr",
              gap: 0,
              minHeight: 420,
            }}
          >
            <Pane
              title="Before"
              ir={leftSnap.ir}
              highlightIds={diff.leftHighlightIds}
              countLabel={`${leftSnap.ir.components.length} comps · ${leftSnap.ir.nets.length} nets`}
            />

            <ChangeLedger diff={diff} />

            <Pane
              title="After"
              ir={rightSnap.ir}
              highlightIds={diff.rightHighlightIds}
              countLabel={`${rightSnap.ir.components.length} comps · ${rightSnap.ir.nets.length} nets`}
              right
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function DiffSummaryChips({
  totals,
}: {
  totals: {
    added: number;
    removed: number;
    changed: number;
    netsTopo: number;
  };
}) {
  const chips: Array<{
    label: string;
    color: "success" | "error" | "warning" | "info";
    n: number;
  }> = [
    { label: "added", color: "success", n: totals.added },
    { label: "removed", color: "error", n: totals.removed },
    { label: "changed", color: "warning", n: totals.changed },
    { label: "net edits", color: "info", n: totals.netsTopo },
  ];
  return (
    <Stack direction="row" spacing={0.5}>
      {chips
        .filter((c) => c.n > 0)
        .map((c) => (
          <Chip
            key={c.label}
            size="small"
            label={`${c.n} ${c.label}`}
            color={c.color}
            variant="outlined"
            sx={{ fontFamily: "mono", fontSize: 10, height: 18 }}
          />
        ))}
    </Stack>
  );
}

function Pane({
  title,
  ir,
  highlightIds,
  countLabel,
  right,
}: {
  title: string;
  ir: import("@models/circuit-ir").CircuitIR;
  highlightIds: ReadonlySet<string>;
  countLabel: string;
  right?: boolean;
}) {
  return (
    <Box
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        borderLeft: right ? `1px solid ${t.palette.border.default}` : 0,
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={(t) => ({
          px: 1.75,
          py: 1,
          borderBottom: `1px solid ${t.palette.border.default}`,
        })}
      >
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", fontSize: 11, letterSpacing: "0.12em" }}
        >
          {title}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          sx={(t) => ({
            color: "text.secondary",
            fontFamily: t.fonts.mono,
          })}
        >
          {countLabel}
        </Typography>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 380 }}>
        <CircuitRenderResolver
          ir={ir}
          height={380}
          highlightIds={highlightIds}
        />
      </Box>
    </Box>
  );
}

function ChangeLedger({
  diff,
}: {
  diff: ReturnType<typeof diffSnapshots>;
}) {
  return (
    <Box
      sx={(t) => ({
        borderLeft: `1px solid ${t.palette.border.default}`,
        borderRight: `1px solid ${t.palette.border.default}`,
        bgcolor: t.palette.surface.sunken,
        overflowY: "auto",
        maxHeight: "min(60vh, 520px)",
      })}
    >
      <Section
        title={`Added · ${diff.componentsAdded.length}`}
        icon={<AddIcon fontSize="small" sx={{ color: "success.main" }} />}
        empty={diff.componentsAdded.length === 0}
      >
        <Stack divider={<Divider sx={{ borderColor: "border.default" }} />}>
          {diff.componentsAdded.map((c) => (
            <CompRow
              key={c.id}
              refdes={c.refdes || "—"}
              kind={c.kind}
              value={c.value}
              tone="ok"
            />
          ))}
        </Stack>
      </Section>

      <Section
        title={`Removed · ${diff.componentsRemoved.length}`}
        icon={<RemoveIcon fontSize="small" sx={{ color: "error.main" }} />}
        empty={diff.componentsRemoved.length === 0}
      >
        <Stack divider={<Divider sx={{ borderColor: "border.default" }} />}>
          {diff.componentsRemoved.map((c) => (
            <CompRow
              key={c.id}
              refdes={c.refdes || "—"}
              kind={c.kind}
              value={c.value}
              tone="err"
            />
          ))}
        </Stack>
      </Section>

      <Section
        title={`Changed · ${diff.componentsChanged.length}`}
        icon={<EditIcon fontSize="small" sx={{ color: "warning.main" }} />}
        empty={diff.componentsChanged.length === 0}
      >
        <Stack divider={<Divider sx={{ borderColor: "border.default" }} />}>
          {diff.componentsChanged.map((c) => (
            <ChangeRow key={c.componentId} change={c} />
          ))}
        </Stack>
      </Section>

      <Section
        title={`Net edits · ${diff.netsTopologyChanged.length}`}
        icon={<RouteIcon fontSize="small" sx={{ color: "info.main" }} />}
        empty={
          diff.netsTopologyChanged.length === 0 &&
          diff.netsAdded.length === 0 &&
          diff.netsRemoved.length === 0
        }
      >
        <Stack divider={<Divider sx={{ borderColor: "border.default" }} />}>
          {diff.netsAdded.map((n) => (
            <Box key={`a-${n.id}`} sx={{ p: 1 }}>
              <Typography
                variant="caption"
                sx={(t) => ({
                  fontFamily: t.fonts.mono,
                  color: "success.main",
                })}
              >
                + {n.name || "(anonymous)"}
              </Typography>
            </Box>
          ))}
          {diff.netsRemoved.map((n) => (
            <Box key={`r-${n.id}`} sx={{ p: 1 }}>
              <Typography
                variant="caption"
                sx={(t) => ({
                  fontFamily: t.fonts.mono,
                  color: "error.main",
                })}
              >
                − {n.name || "(anonymous)"}
              </Typography>
            </Box>
          ))}
          {diff.netsTopologyChanged.map((n) => (
            <NetTopologyRow key={n.netId} change={n} />
          ))}
        </Stack>
      </Section>
    </Box>
  );
}

function Section({
  title,
  icon,
  empty,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  empty: boolean;
  children: React.ReactNode;
}) {
  if (empty) return null;
  return (
    <Box>
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={(t) => ({
          px: 1.25,
          py: 0.75,
          bgcolor: t.palette.surface.raised,
          borderBottom: `1px solid ${t.palette.border.default}`,
        })}
      >
        {icon}
        <Typography
          variant="overline"
          sx={{
            color: "text.secondary",
            fontSize: 11,
            letterSpacing: "0.12em",
          }}
        >
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

function CompRow({
  refdes,
  kind,
  value,
  tone,
}: {
  refdes: string;
  kind: string;
  value: string | null;
  tone: "ok" | "err";
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={(t) => ({
        px: 1.25,
        py: 0.75,
        borderLeft: `2px solid ${
          tone === "ok" ? t.palette.success.main : t.palette.error.main
        }`,
      })}
    >
      <Box
        sx={(t) => ({
          fontFamily: t.fonts.mono,
          fontSize: 12,
          fontWeight: 600,
          color: tone === "ok" ? t.palette.success.main : t.palette.error.main,
          minWidth: 48,
        })}
      >
        {refdes}
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {kind}
      </Typography>
      {value && (
        <Typography
          variant="caption"
          sx={(t) => ({
            color: "text.disabled",
            fontFamily: t.fonts.mono,
            fontSize: 11,
          })}
        >
          · {value}
        </Typography>
      )}
    </Stack>
  );
}

function ChangeRow({ change }: { change: ComponentChange }) {
  return (
    <Box sx={{ px: 1.25, py: 0.75 }}>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Box
          sx={(t) => ({
            fontFamily: t.fonts.mono,
            fontSize: 12,
            fontWeight: 600,
            color: "warning.main",
            minWidth: 48,
          })}
        >
          {change.refdes}
        </Box>
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          {change.changes.length} field
          {change.changes.length === 1 ? "" : "s"}
        </Typography>
      </Stack>
      <Stack spacing={0.25} sx={{ mt: 0.5, pl: 6.5 }}>
        {change.changes.map((c) => (
          <Stack
            // `field` is unique per refdes change row — better than index for
            // diffing if the backend reorders within a change set.
            key={c.field}
            direction="row"
            spacing={0.75}
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: 11,
              alignItems: "baseline",
            })}
          >
            <Box sx={{ color: "text.secondary" }}>{c.field}:</Box>
            <Tooltip title={String(c.before ?? "—")}>
              <Box
                sx={{
                  color: "text.disabled",
                  textDecoration: "line-through",
                  maxWidth: 80,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {String(c.before ?? "—")}
              </Box>
            </Tooltip>
            <Box sx={{ color: "text.disabled" }}>→</Box>
            <Tooltip title={String(c.after ?? "—")}>
              <Box
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  maxWidth: 80,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {String(c.after ?? "—")}
              </Box>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function NetTopologyRow({ change }: { change: NetTopologyChange }) {
  return (
    <Box sx={{ px: 1.25, py: 0.75 }}>
      <Box
        sx={(t) => ({
          fontFamily: t.fonts.mono,
          fontSize: 12,
          color: "info.main",
          fontWeight: 600,
        })}
      >
        {change.name || "(anonymous net)"}
      </Box>
      <Stack spacing={0.25} sx={{ mt: 0.25, pl: 1 }}>
        {change.added.map((ep, i) => (
          <Box
            key={`a-${i}`}
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: 11,
              color: "success.main",
            })}
          >
            + {ep.refdes}.{ep.pin}
          </Box>
        ))}
        {change.removed.map((ep, i) => (
          <Box
            key={`r-${i}`}
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: 11,
              color: "error.main",
            })}
          >
            − {ep.refdes}.{ep.pin}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
